import type { ActivityDay, ActivityTotals, ParentActivity } from '../../../shared/api/parent'
import type { Skill } from '../../../shared/types/prisma-enums'

/**
 * The arithmetic behind C2's charts, in one place and testable.
 *
 * 🔴 EVERY RATIO ON THIS SCREEN SHIPS WITH ITS DENOMINATOR, AND THAT IS THE
 * WHOLE REASON THIS FILE IS SEPARATE FROM THE CHARTS. The parent tier's standing
 * rule — OD-39, contract D4, `ParentService`'s refusal of an "overall %" tile —
 * is that a percentage a parent cannot check is a measurement claim about their
 * child. So nothing here returns a bare number: `accuracy` returns the two
 * counts as well as the percent, `perActiveDay` returns what it divided by, and
 * the screen prints *"38 of 46 answers · 83%"* rather than a gauge.
 *
 * 🔴 AND NOTHING HERE INVENTS A DENOMINATOR IT WAS NOT GIVEN. There is no
 * "expected minutes per day", no target, no benchmark against other children —
 * every one of those would be a number this file made up and a parent would take
 * to a school as fact. The charts compare a child with **themselves**, over the
 * window, which is the only comparison the data supports.
 *
 * Pure, for the reason the rest of this folder is pure: there is no DOM test
 * renderer in this repo (OD-60), so logic that lives in a component is logic
 * nothing can test.
 */

/** A ratio and the two numbers it was made from. Never one without the others. */
export interface Ratio {
  /** The numerator. */
  value: number
  /** The denominator. 🔴 On screen, always. */
  of: number
  /** 0–100, rounded. Meaningless without the two above, which is why it is last. */
  percent: number
}

/**
 * ⚠️ NULL WHEN THERE IS NOTHING TO DIVIDE BY, AND THE SCREEN SAYS SO IN WORDS.
 * "0%" for a child who has not answered a question yet is a score; "no answers
 * yet" is a fact. `SkillProgress` makes the same distinction one component over.
 */
export function ratio(value: number, of: number): Ratio | null {
  if (of <= 0) return null
  return { value, of, percent: Math.round((value / of) * 100) }
}

/** The one ratio this product draws: right answers over answers given. */
export const accuracy = (totals: ActivityTotals): Ratio | null =>
  ratio(totals.correct, totals.answered)

/** Days played, over days in the window. The consistency number. */
export const consistency = (activity: ParentActivity): Ratio | null =>
  ratio(activity.totals.activeDays, activity.windowDays)

/**
 * One bar.
 *
 * ⚠️ `ratio` IS HEIGHT AGAINST THE BUSIEST DAY, NOT AGAINST A TARGET. There is
 * no target — see the header — so the tallest bar is the child's own best day in
 * this window and every other bar is read against it.
 */
export interface ChartBar {
  /** `YYYY-MM-DD`. */
  day: string
  value: number
  /** 0–1 against `max`. Zero stays zero: an empty day draws no bar at all. */
  ratio: number
}

export interface BarSeries {
  bars: ChartBar[]
  /** The busiest day's value. Printed on the axis so the heights mean something. */
  max: number
  total: number
}

/**
 * Scale a per-day series for drawing.
 *
 * ⚠️ A WINDOW WITH NOTHING IN IT GIVES EVERY BAR A RATIO OF ZERO, never a
 * division by zero and never a full-height row of bars. A chart that looked busy
 * for a child who has not played is the worst available failure here.
 */
export function series(days: readonly ActivityDay[], pick: (d: ActivityDay) => number): BarSeries {
  const bars = days.map((d) => ({ day: d.day, value: pick(d), ratio: 0 }))
  const max = bars.reduce((m, b) => Math.max(m, b.value), 0)
  const total = bars.reduce((n, b) => n + b.value, 0)

  return {
    bars: max === 0 ? bars : bars.map((b) => ({ ...b, ratio: b.value / max })),
    max,
    total,
  }
}

/**
 * The window's play, by day of the week.
 *
 * 🔴 IT REPORTS THE DENOMINATOR PER WEEKDAY, WHICH IS THE PART THAT MAKES IT
 * HONEST. A 28-day window holds exactly four of each weekday; a 30-day one holds
 * five Mondays and four Fridays. Comparing raw totals across an uneven window
 * would tell a parent their child "plays most on Mondays" because the window
 * happened to contain more of them.
 */
export interface WeekdayRow {
  /** 0 = Sunday, matching `Date.getUTCDay`. */
  weekday: number
  events: number
  /** How many of this weekday the window contained. The denominator. */
  days: number
  /** Days of this weekday with any play. */
  activeDays: number
}

export function weekdays(days: readonly ActivityDay[]): WeekdayRow[] {
  const rows: WeekdayRow[] = Array.from({ length: 7 }, (_, weekday) => ({
    weekday,
    events: 0,
    days: 0,
    activeDays: 0,
  }))

  for (const day of days) {
    /*
     * ⚠️ PARSED AS UTC AND READ AS UTC. `new Date('2026-08-12')` is already UTC
     * midnight, and `getDay()` would then apply the browser's offset — which in
     * IST is +05:30 and harmless, and in any negative offset moves every square
     * back a weekday. The key is an IST calendar day; reading it in UTC keeps it
     * one.
     */
    const weekday = new Date(`${day.day}T00:00:00.000Z`).getUTCDay()
    const row = rows[weekday]
    row.days += 1
    row.events += day.events
    if (day.events > 0) row.activeDays += 1
  }
  return rows
}

/** One skill's share of the window's play. */
export interface SkillShare {
  skill: Skill
  events: number
  /** 0–1 of every skill-carrying event in the window. */
  share: number
  topLevel: number | null
}

/**
 * What the window was spent on, by skill.
 *
 * ⚠️ THE DENOMINATOR IS EVENTS THAT CARRY A SKILL, NOT ALL EVENTS. Session
 * boundaries carry none, so dividing by every event would make the shares add up
 * to less than one and leave a parent wondering what the missing slice was.
 */
export function skillShares(days: readonly ActivityDay[]): SkillShare[] {
  const bySkill = new Map<Skill, { events: number; topLevel: number | null }>()

  for (const day of days) {
    for (const row of day.skills) {
      const current = bySkill.get(row.skill) ?? { events: 0, topLevel: null }
      current.events += row.events
      current.topLevel =
        row.topLevel === null ? current.topLevel : Math.max(current.topLevel ?? 0, row.topLevel)
      bySkill.set(row.skill, current)
    }
  }

  const total = [...bySkill.values()].reduce((n, s) => n + s.events, 0)

  return [...bySkill.entries()]
    .map(([skill, s]) => ({
      skill,
      events: s.events,
      share: total === 0 ? 0 : s.events / total,
      topLevel: s.topLevel,
    }))
    .sort((a, z) => z.events - a.events || a.skill.localeCompare(z.skill))
}

/** Which game took the most of the window. Null when nothing was played. */
export function topGame(days: readonly ActivityDay[]): { game: string; events: number } | null {
  const byGame = new Map<string, number>()
  for (const day of days) {
    for (const row of day.games) byGame.set(row.game, (byGame.get(row.game) ?? 0) + row.events)
  }

  let best: { game: string; events: number } | null = null
  for (const [game, events] of byGame) {
    /* `>` rather than `>=`, and the map preserves insertion order — so a tie
       keeps the game that got there first rather than the last one iterated. */
    if (best === null || events > best.events) best = { game, events }
  }
  return best
}

/** The busiest single day, for the report's "their best day" line. */
export function busiestDay(days: readonly ActivityDay[]): ActivityDay | null {
  let best: ActivityDay | null = null
  for (const day of days) {
    if (day.events === 0) continue
    if (best === null || day.events > best.events) best = day
  }
  return best
}

/**
 * Minutes per day the child actually played.
 *
 * 🔴 DIVIDED BY **ACTIVE** DAYS, NOT BY THE WINDOW, and the returned `of` says
 * which. "12 minutes a day" across a 28-day window in which a child played nine
 * times is a different claim from "12 minutes on each of the nine days they
 * played" — and the first one, on a screen with no target on it, reads as a
 * score against an expectation nobody set.
 */
export const perActiveDay = (value: number, totals: ActivityTotals): Ratio | null =>
  totals.activeDays === 0
    ? null
    : {
        value,
        of: totals.activeDays,
        percent: Math.round(value / totals.activeDays),
      }

/** `YYYY-MM-DD` → the day, for the calendar's tap. */
export function dayIndex(days: readonly ActivityDay[]): Map<string, ActivityDay> {
  return new Map(days.map((d) => [d.day, d]))
}

/**
 * Did anything at all happen on this day?
 *
 * ⚠️ A SITTING WITH NO EVENTS COUNTS AS SOMETHING. `recorded: false` (I5) writes
 * a session and no events, and so does a child who opened the app and left — a
 * day detail that said "nothing happened" would be contradicting a session row
 * that exists.
 */
export const dayHasSomething = (day: ActivityDay | undefined): boolean =>
  day !== undefined && (day.events > 0 || day.sessions > 0)
